import { reactive } from 'vue';
import axios from 'axios';

// import { useFirstNameValidation } from './nameValidation';

import { RegistrationQueryStringInfo } from './querystringInfo';

export interface GroupInvitationInfo {
  groupName: string;
  adminName: string;
}

export interface ValidationInfo {
  passwordMinLength: number;
  passwordMaxLength: number;
  firstnameMinLength: number;
  firstnameMaxLength: number;
  lastnameMinLength: number;
  lastnameMaxLength: number;
  usernameMinLength: number;
  usernameMaxLength: number;
}

export interface RegistrationServerInfo {
  disabled: boolean;
  captchaApiKey: string;
  codeLength: number;
  notificationEmail: number;
  mobileApp?: boolean;
  mailDomains: string[];
  trial: boolean;
  trialExpired: boolean;
  trialDays: number;
  supportEmail: string;
  ui: Record<string, string>;
  id: string;
  validation: ValidationInfo;
  groupInvitationInfos: GroupInvitationInfo;
}

export enum PANEL {
  HOME,
  RECOVERY,
  CAPTCHA,
  CODE,
  LOADER,

  TRIAL_EXPIRED,
  REJECTED,
}

export interface FormData {
  username: string;
  mailDomain: string;
  firstname: string;
  lastname: string;
  password: string;
  customer: string;
  trialToken: string | undefined;
  locale: string;
  UIFontsPrefs: string;
  UIFontsDefault: string;
  groupInvitation: string | undefined;
  timezone: string;
  source: string | undefined;
  termsOfUseAccepted: boolean;
}

export interface ServerValidationError {
  field: string;
  code: string | undefined;
}

export interface StateHandler {
  displayError(message: string): void;
  onRegistrationDone(data: any): void;
}

interface StateInterface {
  panelToDisplay: PANEL;
  captchaRequired: boolean;
  recoveryEmail: undefined | string;
  serverInfo: RegistrationServerInfo | undefined;

  serverLoading: boolean;
  serverErrorsHome: ServerValidationError[] | undefined;
  serverErrorsRecovery: ServerValidationError[] | undefined;
  serverErrorsCode: ServerValidationError[] | undefined;
}

interface StoreInterface {
  formData: FormData | undefined;
  captchaKey: undefined | string;

  state: StateInterface;
  handler: StateHandler | undefined;

  init(handler: StateHandler): void;

  fetchServerInfo(regInfo: RegistrationQueryStringInfo): Promise<RegistrationServerInfo>;
  onFormSubmitted(data: FormData): void;
  onRecoveryEmail(email: string): void;
  onCaptchaResolved(captchaKey: string): Promise<void>;
  onCodeSubmitted(code: string): Promise<boolean>;
  goBack(): void;
  onResendCode(): Promise<void>;
  logDisplayedScreen(qsInfo: RegistrationQueryStringInfo, panel: PANEL): void;

  // private

  validateExternalAddress(recoveryEmail: string): Promise<boolean>;
  preRegistration(): void;
  preRegistrationRequest(): Promise<boolean>;
  displayPanelWithError(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processServerValidationErrors(errors: any): void;
  processServerError(err: ServerValidationError): boolean;
  displayError(err: string): void;
}

/**
 * Store
 */
const store: StoreInterface = {
  formData: undefined,
  handler: undefined,
  captchaKey: undefined,

  state: reactive({
    panelToDisplay: PANEL.HOME,
    recoveryEmail: undefined, // state because used in code panel
    captchaRequired: true,
    serverLoading: false,
    serverInfo: undefined,

    serverErrorsHome: undefined,
    serverErrorsRecovery: undefined,
    serverErrorsCode: undefined,
  }),

  init(handler: StateHandler) {
    this.handler = handler;

    // reset state

    this.formData = undefined;
    this.captchaKey = undefined;
    this.state.recoveryEmail = undefined;
    this.state.panelToDisplay = PANEL.HOME;
    this.state.captchaRequired = true;
    this.state.serverLoading = false;

    this.state.serverErrorsHome = undefined;
    this.state.serverErrorsRecovery = undefined;
    this.state.serverErrorsCode = undefined;

    // do not clear server info here!
    // this.state.serverInfo = undefined;
  },

  async fetchServerInfo(regInfo: RegistrationQueryStringInfo): Promise<RegistrationServerInfo> {
    if (this.state.serverInfo) {
      return this.state.serverInfo;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestHeaders: any = {
      lf: regInfo.lf,
    };

    if (regInfo.token) {
      requestHeaders.token = regInfo.token;
    }

    if (regInfo.source) {
      requestHeaders.source = regInfo.source;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {};
    if (regInfo.groupInvitation) {
      params.oi = regInfo.groupInvitation;
    }

    const axiosResponse = await axios.get('/api/v1/account/registration', {
      params,
      headers: requestHeaders,
    });

    this.state.serverInfo = axiosResponse.data;

    return this.state.serverInfo as RegistrationServerInfo;
  },

  onFormSubmitted(data: FormData) {
    this.formData = data;
    this.state.serverErrorsHome = undefined;
    this.state.panelToDisplay = PANEL.RECOVERY;
  },

  async onRecoveryEmail(email: string) {
    this.state.recoveryEmail = email;

    /*
     if (this.state.serverLoading) {
      return;
    }
    this.state.serverLoading = true;
    const isValid = await this.validateExternalAddress(email);
    this.state.serverLoading = false;

    if (!isValid) {
      return;
    }
*/

    if (this.state.captchaRequired) {
      this.state.panelToDisplay = PANEL.CAPTCHA;
    } else {
      await this.preRegistration();
    }
  },

  async onResendCode() {
    this.state.panelToDisplay = PANEL.LOADER;

    setTimeout(async () => {
      await this.onRecoveryEmail(this.state.recoveryEmail || '');
    }, 3000);
  },

  async onCaptchaResolved(captchaKey: string) {
    this.captchaKey = captchaKey;
    await this.preRegistration();
  },

  displayError(message: string) {
    this.handler?.displayError(message);
  },

  goBack() {
    if (this.state.serverLoading) {
      // Don't show form if processing
      return;
    }

    switch (this.state.panelToDisplay) {
      case PANEL.RECOVERY:
        this.state.panelToDisplay = PANEL.HOME;
        break;

      case PANEL.CODE:
        this.state.panelToDisplay = PANEL.RECOVERY;
        break;

      default:
        break;
    }
  },

  /**
   * Server call
   */

  async validateExternalAddress(recoveryEmail: string) {
    try {
      const axiosResponse = await axios.post(
        `/api/v1/account/registration/notification`,
        { customer: this.formData?.customer, recoveryEmail },
        {
          validateStatus(status: number) {
            return status < 500; // Resolve only if the status code is less than 500
          },
        },
      );

      if (axiosResponse.status === 200) {
        return true;
      }

      if (axiosResponse.status === 400 && axiosResponse.data) {
        const errors = axiosResponse.data && axiosResponse.data.errors;
        this.processServerValidationErrors(errors);
        this.displayPanelWithError();
        return false;
      }
      // return true by default (not implemented in all apis )
      return true;
    } catch (err) {
      //
      return true;
    }
  },

  async preRegistration() {
    this.state.serverLoading = true;

    const isRegistered = await this.preRegistrationRequest();
    this.state.serverLoading = false;

    // Display correct panel
    if (isRegistered) {
      this.state.panelToDisplay = PANEL.CODE;
    } else {
      this.displayPanelWithError();
    }
  },

  displayPanelWithError() {
    if (this.state.serverErrorsHome) {
      this.state.panelToDisplay = PANEL.HOME;
    } else if (this.state.serverErrorsRecovery) {
      this.state.panelToDisplay = PANEL.RECOVERY;
    } else if (this.state.serverErrorsCode) {
      this.state.panelToDisplay = PANEL.CODE;
      // the process can display some page
    } else if (this.state.panelToDisplay !== PANEL.REJECTED && this.state.panelToDisplay !== PANEL.TRIAL_EXPIRED) {
      this.state.panelToDisplay = PANEL.HOME;
    }
  },

  async preRegistrationRequest(): Promise<boolean> {
    try {
      const formObject = {
        recoveryEmail: this.state.recoveryEmail,
        captcha: this.captchaKey,
        ...this.formData,
      };

      if (!this.state.captchaRequired) {
        formObject.captcha = undefined; // else the backend validate the captcha
      }

      this.state.serverErrorsHome = undefined;
      this.state.serverErrorsRecovery = undefined;
      this.state.serverErrorsCode = undefined;

      const axiosResponse = await axios.post(`/api/v1/account/registration`, formObject, {
        validateStatus(status: number) {
          return status < 500; // Resolve only if the status code is less than 500
        },
      });

      if (axiosResponse && axiosResponse.headers) {
        const rateLimit = axiosResponse.headers['x-ratelimit-remaining'] || axiosResponse.headers['X-RateLimit-Remaining'];
        if (rateLimit && Number(rateLimit) > 0) {
          this.state.captchaRequired = false;
        } else {
          this.state.captchaRequired = true;
        }
      }

      if (axiosResponse.status === 200) {
        return true;
      }

      if (axiosResponse.status === 403) {
        this.state.panelToDisplay = PANEL.REJECTED;
        return false;
      }

      if (axiosResponse.status === 400 && axiosResponse.data) {
        const errors = axiosResponse.data && axiosResponse.data.errors;
        this.processServerValidationErrors(errors);
        return false;
      }

      // Other error
      const errorMessage = `${axiosResponse.status}: ${axiosResponse.statusText || ''}`;
      this.displayError(errorMessage);
      return false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log('Unable to query server:', err, err.response);
      let errorMessage = err.message;
      if (err.response) {
        errorMessage = `${err.response.status}: ${err.response.statusText || ''}`;
      }
      this.displayError(errorMessage);
      return Promise.resolve(false);
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processServerValidationErrors(errors: any) {
    this.state.serverErrorsHome = undefined;
    this.state.serverErrorsRecovery = undefined;

    if (errors) {
      for (let i = 0; i < errors.length; i += 1) {
        const err = errors[i];
        const { code } = err;
        let { field = '' } = err;
        field = field.toLowerCase();

        const serverError: ServerValidationError = {
          field,
          code,
        };

        if (field === 'trial') {
          this.state.panelToDisplay = PANEL.TRIAL_EXPIRED;
          break;
        }

        const isProcessed = this.processServerError(serverError);

        if (!isProcessed) {
          this.displayError(`${field}: ${code}`);
          break;
        }
      }
    } else {
      this.displayError('');
    }
  },

  processServerError(err: ServerValidationError): boolean {
    const { field } = err;

    switch (field) {
      case 'firstname':
      case 'lastname':
      case 'accountname':
      case 'password':
        if (!this.state.serverErrorsHome) {
          this.state.serverErrorsHome = [];
        }
        this.state.serverErrorsHome.push(err);
        break;

      case 'recoveryemail':
        if (!this.state.serverErrorsRecovery) {
          this.state.serverErrorsRecovery = [];
        }

        this.state.serverErrorsRecovery.push(err);
        break;

      default:
        return false;
    }
    return true;
  },

  /**
   * Code
   */

  async onCodeSubmitted(code: string): Promise<boolean> {
    this.state.serverErrorsCode = undefined;
    this.state.serverLoading = true;

    const addServerErrorCode = (err: ServerValidationError) => {
      if (!this.state.serverErrorsCode) {
        this.state.serverErrorsCode = [];
      }
      this.state.serverErrorsCode.push(err);
    };

    try {
      const axiosCodeResponse = await axios.post(
        `/api/v1/account/registration/verification?code=${code}`,
        {},
        {
          validateStatus(status: number) {
            return status < 500; // Resolve only if the status code is less than 500
          },
        },
      );

      if (axiosCodeResponse.status === 200) {
        this.handler?.onRegistrationDone(axiosCodeResponse.data);
        return true;
      }

      // server loadinf false for error only
      this.state.serverLoading = false;

      if (axiosCodeResponse.status === 401) {
        addServerErrorCode({ field: 'code', code: 'INVALID_CODE' });
        return false;
      }

      if (axiosCodeResponse.status === 400) {
        // validation failure (we should display the registration screen)
        // validation errors are optional
        const errors = axiosCodeResponse.data && axiosCodeResponse.data.errors;
        this.processServerValidationErrors(errors);
        this.displayPanelWithError();
        return false;
      }

      if (axiosCodeResponse.status === 429) {
        this.displayError('Maximum attemps reached');
        this.state.panelToDisplay = PANEL.HOME;
        return false;
      }

      if (axiosCodeResponse.status === 403) {
        this.state.panelToDisplay = PANEL.REJECTED;
        return false;
      }

      const errorMessage = `${axiosCodeResponse.status}: ${axiosCodeResponse.statusText || ''}`;
      this.displayError(errorMessage);
      return false;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.log('Unable to query server:', err, err.response);
      let errorMessage = err.message;
      if (err.response) {
        errorMessage = `${err.response.status}: ${err.response.statusText || ''}`;
      }
      this.displayError(errorMessage);
      this.state.serverLoading = false;
      return Promise.resolve(false);
    }
  },

  /**
   * LOg
   */

  async logDisplayedScreen(qsInfo: RegistrationQueryStringInfo, panel: PANEL) {
    if (!this.state.serverInfo) {
      return;
    }

    const { id: uid } = this.state.serverInfo;

    try {
      let data = null;

      switch (panel) {
        case PANEL.HOME:
          data = 'form';
          break;

        case PANEL.RECOVERY:
          data = 'recovery';
          break;

        case PANEL.CODE:
          data = 'code';
          break;

        case PANEL.CAPTCHA:
          data = 'captcha';
          break;

        default:
          data = null;
      }

      if (!data) {
        return;
      }

      await axios.post(
        `/api/v1/account/registration/s`,
        {},
        {
          headers: {
            s: data,
            uid,
            lf: qsInfo.lf,
          },
        },
      );
    } catch (err) {
      //
    }
  },
};

export default store;
